import React, { useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { format, fromUnixTime } from 'date-fns';
import { MeasurementDetailItem, MeasurementGroupType } from '../index';

type DownloadMeasurementsDataProps = {
  unit: string;
  data: MeasurementDetailItem[];
  measurementGroup: MeasurementGroupType;
  selectedPhases: number[];
  isSummary?: boolean;
};

export const DownloadMeasurementsData: React.FC<DownloadMeasurementsDataProps> = ({
  unit,
  data,
  measurementGroup,
  selectedPhases,
  isSummary,
}) => {
  const { t } = useTranslation('channel-details');

  const headers = useMemo(() => {
    return [
      {
        key: 'timestamp',
        label: t('time'),
      },
      {
        key: 'date',
        label: t('date'),
      },
      ...(isSummary || measurementGroup === MeasurementGroupType.Absolute
        ? [{ key: 'value', label: t('value') }]
        : [
            ...(selectedPhases.includes(1) ? [{ key: 'value1', label: `${t('phase')} 1` }] : []),
            ...(selectedPhases.includes(2) ? [{ key: 'value2', label: `${t('phase')} 2` }] : []),
            ...(selectedPhases.includes(3) ? [{ key: 'value3', label: `${t('phase')} 3` }] : []),
          ]),
      { key: 'unit', label: t('unit') },
    ];
  }, [isSummary, measurementGroup, selectedPhases]);

  const dataWithUnits = useMemo(() => {
    return data.map((item) => ({
      ...item,
      date: format(fromUnixTime(item.timestamp), 'dd.MM.yyyy HH:mm:ss'),
      ...(isSummary || measurementGroup === MeasurementGroupType.Absolute
        ? { value: item.value.toString().replace(/\./g, ',') }
        : {
            value1: item?.phasesValues?.[0]?.toString().replace(/\./g, ',') || '',
            value2: item?.phasesValues?.[1]?.toString().replace(/\./g, ',') || '',
            value3: item?.phasesValues?.[2]?.toString().replace(/\./g, ',') || '',
          }),
      unit,
    }));
  }, [isSummary, data, measurementGroup]);

  return (
    <div className="center-button-container ">
      <CSVLink
        className="button m-b-24"
        filename={`measurements-${new Date().getTime()}.csv`}
        target="_blank"
        data={dataWithUnits}
        headers={headers}
      >
        {t('downloadMeasurementsData')}
      </CSVLink>
    </div>
  );
};
